type DocumentNavigationTimingLike = {
  responseStart: number
  responseEnd: number
  domInteractive: number
}

type NextJsResourceTimingLike = {
  name: string
  startTime: number
  responseEnd: number
}

export type DocumentLoadRumFields = {
  html_response_start_since_navigation_ms?: number
  html_response_end_since_navigation_ms?: number
  dom_interactive_since_navigation_ms?: number
}

export type NextJsLoadRumFields = {
  next_js_first_request_start_since_navigation_ms?: number
  next_js_last_response_end_since_navigation_ms?: number
}

const roundMs = (value: number) => Math.round(Math.max(0, value) * 100) / 100

const toMilestone = (value: number) =>
  Number.isFinite(value) && value > 0 ? roundMs(value) : undefined

const getNavigationTiming = (): DocumentNavigationTimingLike | undefined => {
  try {
    return performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
  } catch {
    return undefined
  }
}

const getResourceTimings = (): readonly NextJsResourceTimingLike[] => {
  try {
    return performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[]
  } catch {
    return []
  }
}

const getCurrentOrigin = () => {
  try {
    return window.location.origin
  } catch {
    return ''
  }
}

const isSameOriginNextJs = (name: string, origin: string) => {
  try {
    const url = new URL(name)
    return (
      url.origin === origin &&
      url.pathname.startsWith('/_next/static/') &&
      url.pathname.endsWith('.js')
    )
  } catch {
    return false
  }
}

/**
 * 读取 document Navigation Timing 的原始里程碑。
 *
 * 保留时间点而不是重复上报派生耗时，SQL 可直接计算 TTFB、HTML 下载和 HTML
 * 解析耗时，并避免耗尽 QuickTracking 的属性额度。
 */
export const collectDocumentLoadRumFields = (
  navigation = getNavigationTiming()
): DocumentLoadRumFields => {
  if (!navigation) return {}

  const responseStart = toMilestone(navigation.responseStart)
  const responseEnd = toMilestone(navigation.responseEnd)
  const domInteractive = toMilestone(navigation.domInteractive)

  return {
    ...(typeof responseStart === 'number'
      ? { html_response_start_since_navigation_ms: responseStart }
      : {}),
    ...(typeof responseEnd === 'number'
      ? { html_response_end_since_navigation_ms: responseEnd }
      : {}),
    ...(typeof domInteractive === 'number'
      ? { dom_interactive_since_navigation_ms: domInteractive }
      : {}),
  }
}

/**
 * 汇总 Home 首次挂载前发起的同源 Next JS 请求边界。
 *
 * cutoff 使用 Home mount，避免媒体完成前才加载的活动弹窗 / 懒加载 chunk 污染冷启动
 * JS 瀑布；URL 仅用于本地分类，不会上报。
 */
export const collectNextJsLoadRumFields = (
  entries: readonly NextJsResourceTimingLike[] = getResourceTimings(),
  origin = getCurrentOrigin(),
  requestedBeforeMs = Number.POSITIVE_INFINITY
): NextJsLoadRumFields => {
  let firstRequestStart = Number.POSITIVE_INFINITY
  let lastResponseEnd = 0

  for (const entry of entries) {
    if (!isSameOriginNextJs(entry.name, origin)) continue
    if (!Number.isFinite(entry.startTime) || entry.startTime < 0) continue
    if (entry.startTime > requestedBeforeMs) continue
    if (!Number.isFinite(entry.responseEnd) || entry.responseEnd <= 0) continue

    firstRequestStart = Math.min(firstRequestStart, entry.startTime)
    lastResponseEnd = Math.max(lastResponseEnd, entry.responseEnd)
  }

  if (!Number.isFinite(firstRequestStart) || lastResponseEnd <= 0) return {}

  return {
    next_js_first_request_start_since_navigation_ms: roundMs(firstRequestStart),
    next_js_last_response_end_since_navigation_ms: roundMs(lastResponseEnd),
  }
}
